import { useEvent } from "@/context/event-context";
import { useGalleryImages } from "@/hooks/use-gallery-images";
import { useEffect, useState, useRef } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import forthecityLogo from "@/assets/forthecity-logo.png";

const NumberBadge = ({ n }: { n: number }) => (
  <span className="inline-flex items-center gap-2 shrink-0">
    <span className="w-[18px] h-[18px] rounded-full bg-ink text-white text-[10px] font-heading font-bold flex items-center justify-center shrink-0">
      {n}
    </span>
    <span className="w-[15px] h-px bg-ink" />
  </span>
);

// ============================================
// Logo intro → Vertical photo marquee
// ============================================
function LogoMarquee({ eventId }: { eventId?: string }) {
  const { data: dbImages } = useGalleryImages(eventId, "marquee");
  const marqueeUrls = (dbImages && dbImages.length > 0)
    ? dbImages.map((img) => img.url)
    : [];
  const hasMarquee = marqueeUrls.length > 0;

  const [phase, setPhase] = useState<"logo" | "transition" | "marquee">("logo");
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const hasTriggered = useRef(false);

  // Trigger animation when scrolled into view
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggered.current) {
          setInView(true);
          hasTriggered.current = true;
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Sequence: logo (3s) → transition (0.8s) → marquee.
  // If no images are available, stay on the logo so we never show broken cells.
  useEffect(() => {
    if (!inView || !hasMarquee) return;
    const t1 = setTimeout(() => setPhase("transition"), 3000);
    const t2 = setTimeout(() => setPhase("marquee"), 3800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [inView, hasMarquee]);

  return (
    <div
      ref={containerRef}
      className="w-full aspect-[3/4] lg:aspect-[3/4] relative overflow-hidden bg-ink order-first lg:order-last"
    >
      {/* Phase 1: Logo fades in centered */}
      <div
        className={`absolute inset-0 flex items-center justify-center z-20 transition-all duration-700 ease-out ${
          phase === "logo"
            ? inView ? "opacity-100 scale-100" : "opacity-0 scale-90"
            : "opacity-0 -translate-y-16 scale-95"
        }`}
      >
        <div className="px-10 w-full max-w-[300px] lg:max-w-[360px]">
          <img
            src={forthecityLogo}
            alt="For The City Entertainment"
            width={1920}
            height={1114}
            className="w-full h-auto invert"
          />
        </div>
      </div>

      {/* Phase 2+3: Vertical scrolling photos — only render if we have images */}
      {hasMarquee && (
      <div
        className={`absolute inset-0 z-10 transition-opacity duration-1000 ${
          phase === "marquee" ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="animate-vscroll flex flex-col gap-2">
          {[...marqueeUrls, ...marqueeUrls].map((src, i) => (
            <div key={i} className="w-full aspect-square shrink-0 overflow-hidden">
              <img
                src={src}
                alt=""
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {/* Edge fades */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none" />

        {/* Ghost logo watermark */}
        <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
          <div className="w-[100px] lg:w-[140px] opacity-[0.06]">
            <img src={forthecityLogo} alt="" width={1920} height={1114} className="w-full h-auto" />
          </div>
        </div>
      </div>
      )}

      <style>{`
        @keyframes vscroll {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
        .animate-vscroll {
          animation: vscroll 50s linear infinite;
        }
        .animate-vscroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}

const EventDetailsSkeleton = () => (
  <section className="bg-white py-[8vw]">
    <div className="max-w-[1600px] mx-auto px-[4vw]">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[8vw] items-center">
        <div className="flex flex-col gap-12">
          <Skeleton className="h-14 w-80" />
          <div className="w-full h-px bg-ink/15" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="grid grid-cols-[140px_1fr] gap-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-16 w-full" />
            </div>
          ))}
        </div>
        <Skeleton className="w-full aspect-[3/4]" />
      </div>
    </div>
  </section>
);

const EventDetails = () => {
  const { event, isLoading } = useEvent();

  if (isLoading) return <EventDetailsSkeleton />;
  if (!event) return null;

  const tc = event.theme_config || {};

  const details = [
    {
      label: "The Vision",
      desc: tc.vision || "A curated celebration of style, music, and elevated energy — designed for those who set the standard.",
    },
    {
      label: "The Venue",
      desc: tc.venue || "A landmark venue reimagined for one day of refined indulgence. Expect architectural beauty, ambient lighting, and a layout crafted for flow.",
    },
    {
      label: "Soundtrack",
      desc: tc.soundtrack || "Expect classic hits, modern anthems, and feel good tracks that will have everyone singing word for word curated by world class DJ Von (Homicide).",
    },
  ];

  return (
    <section data-nav-theme="light" className="bg-white py-12 sm:py-[8vw]">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-[4vw]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-[8vw] items-center">
          {/* Text Content */}
          <div className="flex flex-col gap-8 sm:gap-12">
            <h2
              className="font-serif-elegant text-foreground"
              style={{
                fontSize: "var(--section-size, clamp(2.5rem, 5vw, 4.5rem))",
                fontWeight: "var(--section-weight, 400)",
                fontStyle: "var(--section-style, italic)" as any,
                letterSpacing: "var(--section-tracking, 0)",
                lineHeight: 1.1,
              }}
            >
              A Curated Afternoon
            </h2>

            <div className="w-full h-px bg-ink/15" />

            {details.map((d, i) => (
              <div key={d.label} className="grid grid-cols-1 sm:grid-cols-[120px_1fr] md:grid-cols-[140px_1fr] gap-2 sm:gap-4 items-baseline">
                <div className="flex items-center gap-2">
                  <NumberBadge n={i + 1} />
                  <span className="font-heading text-[0.7rem] uppercase tracking-[0.2em] font-extrabold text-foreground">
                    {d.label}
                  </span>
                </div>
                <p
                  className="font-serif-elegant text-foreground/80 italic"
                  style={{
                    fontSize: "var(--body-size, 1.35rem)",
                    lineHeight: "var(--body-line-height, 1.65)",
                  }}
                >
                  {d.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Logo animation → Vertical photo marquee */}
          <LogoMarquee eventId={event.id} />
        </div>
      </div>
    </section>
  );
};

export default EventDetails;
